import { getBrand } from '@alto/design-system';
import { platform } from './getPlatform';

type BackendSelector =
  | { env: 'local' | 'integration' | 'production' | 'staging' }
  | { env: 'sandbox'; sandboxName: string };

type BrandUrls = {
  API_URL: string;
  API_WEBSOCKET_URL: string;
};

const brandDomain = () => {
  // TODO: getBrand() fails when called from tests via deduplicator.ts => config.ts; we
  // should either arrange for a valid window.location.hostname mock, or ensure backendEnv
  // is set to local/integration; for now just default to alto
  let brand = 'alto';
  try {
    brand = getBrand();
  } catch {
    /* pass */
  }

  return {
    alto: 'alto.com',
    fuzehealth: 'pharmacy.fuzehealth.com',
  }[brand];
};

const getLocalUrl = () => {
  const brand = getBrand();
  const BRAND_CONFIG = {
    alto: {
      API_URL: {
        android: 'http://10.0.2.2:3000',
        ios: 'http://patients.alto.local.alto.com:3000',
        web: 'https://patients.alto.local.alto.com:3443',
      },
      API_WEBSOCKET_URL: 'wss://api.alto.local.alto.com:7000/socket/stream?method=POST',
    },
    fuzehealth: {
      API_URL: {
        android: 'http://10.0.2.2:3000',
        ios: 'http://patients.local.pharmacy.fuzehealth.com:3000',
        web: 'https://patients.local.pharmacy.fuzehealth.com:3443',
      },
      API_WEBSOCKET_URL: 'wss://api.alto.local.alto.com:7000/socket/stream?method=POST',
    },
  };

  const config = BRAND_CONFIG[brand];

  switch (platform) {
    case 'web':
    case 'android':
    case 'ios':
      return {
        API_URL: config.API_URL[platform] ?? config.API_URL.web,
        API_WEBSOCKET_URL: config.API_WEBSOCKET_URL,
      } as const;
    default: // windows, macOS not used
      throw new Error(`unexpected platform "${platform}"`);
  }
};

// urls for static environments (stg & prod)
type Env = 'stg' | 'prod';
const getEnvUrl = (env: Env) => {
  const domain = `${env}.${brandDomain()}`;
  // TODO: clean up api hostname stuff and remove suffix
  const apiSuffix = env === 'stg' ? '2' : '';
  return {
    API_URL: `https://patients.${domain}`,
    API_WEBSOCKET_URL: `wss://api${apiSuffix}.${domain}/pubsub/socket/stream?method=POST`,
  } as const;
};

const getSandboxUrl = (sandboxName: string) => {
  let slug = 'unknown-slug'; // there's no useful default, so make it explicit that something's wrong
  let domain = `dev.${brandDomain()}`; // XXX: mobile app config for demo (stg) envs is not supported

  if (platform === 'web') {
    try {
      const match = /^app-([^.]+)\.(.*)$/.exec(window.location.hostname);
      if (match) {
        [, slug, domain] = match;
      }
    } catch (error) {
      console.error('Failed to determine sandbox slug and domain from hostname:', error);
    }
  }

  if (sandboxName && sandboxName !== 'default') {
    slug = sandboxName;
  }

  return {
    API_URL: `https://patients-${slug}.${domain}`,
    API_WEBSOCKET_URL: `wss://api-${slug}.${domain}/pubsub/socket/stream?method=POST`,
  } as const;
};

export const getBaseURL = (opts: BackendSelector): BrandUrls => {
  switch (opts.env) {
    case 'local':
    case 'integration':
      return getLocalUrl();
    case 'production':
      return getEnvUrl('prod');
    case 'sandbox':
      return getSandboxUrl(opts.sandboxName);
    case 'staging':
      return getEnvUrl('stg');
    default: {
      // return prod by default for safety
      return getEnvUrl('prod');
    }
  }
};
